import { i18nDynamic } from 'shared/i18n';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeList as List } from 'react-window';
import { Modal, Button } from 'react-bootstrap';
import {
  useSuggestTableTags,
  Linkrole,
  AiPrediction,
  useTagsByDocument,
  useDocumentTaxonomyConfig,
  useUpdateDocumentTaxonomyConfig,
  usePresentationTree,
} from 'entities/tag';
import { useApplyCellTags } from './Footer/useApplyCellTags';
import { useTagDisplay } from 'app/state/contexts/TagContext';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import Spinner from 'shared/feedback/Spinner';
import { StyledDataSheet } from 'app/components';
import { RawCell } from 'app/utils/tableCells';
import { useAiCellPicker, MAX_AI_CELLS } from './useAiCellPicker';
import { useAiSuggestions } from './useAiSuggestions';
import './AiApplicationModal.scss';
import { Typography } from 'app/components/ui';
import { LinkroleCell, linkroleLabel } from './LinkroleCell';
import { useAiLinkroleFilter } from './useAiLinkroleFilter';
import { LinkroleFilterTree } from './LinkroleFilterTree';
import { resolveConceptLabel } from 'app/utils/tag-utils';
import { TaxonomyLanguages } from 'app/types/editor-taxonomy';
import { AiResultTabs } from './AiResultTabs';
import {
  flattenLista,
  flattenRad,
  flattenKoncept,
  conceptGroupEligibleKeys,
  groupCheckState,
  rowKey,
  ResultTab,
  GroupedRow,
  PredRow,
} from './aiResultGrouping';
import { useEditorReferences } from 'app/api/documents/useEditorData';
import { useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorCoarse, useEditorLiveSection } from 'app/state/contexts/EditorUiContext';
import { ApiError } from 'shared/api/client';

interface AiApplicationModalProps {
  show: boolean;
  onClose: () => void;
  referenceDataSuperId: string;
  documentSuperId: number;
  tableTitle?: string;
}

const ROW_HEIGHT = 52;
const LIST_HEIGHT = Math.round((typeof window !== 'undefined' && window.innerHeight ? window.innerHeight : 800) * 0.66);

export const AiApplicationModal = ({ show, onClose, referenceDataSuperId, documentSuperId, tableTitle }: AiApplicationModalProps) => {
  const { t } = useTranslation('tag');

  const { mutate: predict, data, isPending: isLoading, error, reset } = useSuggestTableTags();
  const { data: tagsData } = useTagsByDocument(documentSuperId, { include: ['concept', 'metadata', 'unit', 'context'] });
  const applyCellTags = useApplyCellTags();
  const section = useEditorLiveSection();
  const { activeSection } = useEditorCoarse();
  const locale = useEditorLocale();
  const references = useEditorReferences();
  const { data: config } = useDocumentTaxonomyConfig(documentSuperId);
  const taxonomyId = config?.taxonomySelections?.find((s) => s.isActive)?.taxonomy?.id ?? null;
  const { tagLanguage = 'sv' } = useTagDisplay();
  const { data: presentationTree } = usePresentationTree(documentSuperId);
  const { mutate: updateConfig } = useUpdateDocumentTaxonomyConfig();

  const { linkroleOverrides, linkroleTree, ancestorIndex, selectedLinkroles, selectedTokens, toggleLinkrole, linkroleRows, persistLinkroleFilter } =
    useAiLinkroleFilter({
      show,
      config,
      presentationTree,
      tagLanguage: tagLanguage ?? 'sv',
      taxonomyId,
      referenceDataSuperId,
      updateConfig,
    });

  const linkroleFilterControl = <LinkroleFilterTree forest={linkroleTree} selected={selectedLinkroles} onToggle={toggleLinkrole} />;

  const refData = useMemo(() => references?.find((r) => r.superId === referenceDataSuperId), [references, referenceDataSuperId]);

  const grid: RawCell[][] = useMemo(() => refData?.selectionContent ?? [], [refData]);

  const { sheetData, requestCells, cappedLocally, handlePickClick, titleToSend, displayTitle, textOf, rowTextOf, colTextOf } = useAiCellPicker({
    show,
    grid,
    tableTitle,
    refDataName: refData?.name,
    reset,
  });

  const runPredict = () => {
    if (!requestCells.length) return;
    persistLinkroleFilter();
    predict({ referenceDataSuperId, documentSuperId, cells: requestCells.slice(0, MAX_AI_CELLS), ...(titleToSend ? { title: titleToSend } : {}) });
  };

  const {
    checked,
    toggle,
    markAll,
    unmarkAll,
    toggleConceptGroup,
    selectedConceptCells,
    checkedCount,
    totalSelectable,
    allSelected,
    selectAllRef,
    cellGroups,
    conceptOrderIndex,
  } = useAiSuggestions({
    data,
    tagsData,
    refData,
    presentationTree,
    selectedLinkroles,
    linkroleOverrides,
    linkroleRows,
    selectedTokens,
    ancestorIndex,
    textOf,
    rowTextOf,
    colTextOf,
  });

  const selectAllCheckbox = (
    <input
      ref={selectAllRef}
      type="checkbox"
      aria-label={t('ai.selectAll')}
      checked={allSelected}
      disabled={totalSelectable === 0}
      onChange={() => (allSelected ? unmarkAll() : markAll())}
    />
  );

  const handleApply = async () => {
    if (!data || !taxonomyId) return;

    const cells = selectedConceptCells;
    if (cells.length === 0) return;

    const sectionContext = { documentSuperId, sectionSuperId: section.superId, pageId: activeSection.uuid ?? '' };
    const res = await applyCellTags({
      mode: 'create',
      cells,
      referenceDataSuperId,
      conceptId: cells[0].conceptId, // fallback; each cell carries its own conceptId
      type: 'nonFraction',
      taxonomyId,
      sectionContext,
      locale,
      origin: 'ai',
    });
    const created = res?.createdCount ?? 0;

    dispatchMessage(EventType.ADD_MESSAGE, { type: 'success', text: `${created} ${t('ai.applied')}`, removeTime: 4000 });
    if (created < cells.length) {
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: `${cells.length - created} ${t('ai.applied.partial')}`, removeTime: 6000 });
    }
    onClose();
  };

  const [activeTab, setActiveTab] = useState<ResultTab>('rad');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const [prevData, setPrevData] = useState(data);
  if (prevData !== data) {
    setPrevData(data);
    setExpandedGroups(new Set());
  }

  const displayRows = useMemo<GroupedRow[]>(() => {
    if (activeTab === 'lista') return flattenLista(cellGroups);
    if (activeTab === 'koncept') return flattenKoncept(cellGroups, conceptOrderIndex, expandedGroups);
    return flattenRad(cellGroups, expandedGroups);
  }, [activeTab, cellGroups, conceptOrderIndex, expandedGroups]);

  const filteredToEmpty = selectedLinkroles.length > 0 && cellGroups.length > 0 && !cellGroups.some((c) => c.preds.length > 0);

  const listRef = useRef<List>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const sizeMap = useRef<Record<number, number>>({});
  const setSize = useCallback((index: number, size: number) => {
    if (size > 0 && sizeMap.current[index] !== size) {
      sizeMap.current[index] = size;
      listRef.current?.resetAfterIndex(index);
    }
  }, []);
  const getItemSize = (index: number) => sizeMap.current[index] ?? ROW_HEIGHT;

  useEffect(() => {
    sizeMap.current = {};
    setExpandedKey(null);
    listRef.current?.resetAfterIndex(0);
  }, [displayRows]);

  const descOf = (p: AiPrediction) =>
    (p.labels && resolveConceptLabel({ languages: p.labels as TaxonomyLanguages, currentLang: tagLanguage ?? 'sv' })) || p.label || p.conceptName;

  const renderPredRow = (it: PredRow) => {
    const { p, lr, rows, key, applied, origin, value, text } = it;
    const winningSibling = rows.find((other: Linkrole) => other.uri !== lr.uri && checked.has(rowKey(it.row, it.col, p.conceptId, other.uri)));
    const siblingChecked = !!winningSibling;
    const disabled = applied || siblingChecked;
    const isExpanded = expandedKey === key;
    const descLabel = descOf(p);
    const linkroleText = linkroleLabel(lr, { tagLanguage, alias: linkroleOverrides[lr.uri]?.alias });
    const originText = i18nDynamic(`tag:ai.origin.${applied ? origin : 'ai'}`);
    const toggleExpand = () => setExpandedKey((cur) => (cur === key ? null : key));
    const indented = activeTab !== 'lista';
    return (
      <>
        <div className={`ai-modal__rowmain${indented ? ' ai-modal__rowmain--member' : ''}`} onClick={toggleExpand}>
          <div role="cell" className="ai-modal__col-check" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={applied || checked.has(key)}
              disabled={disabled}
              onChange={() => toggle(key)}
              aria-label={`${p.conceptName} ${lr.uri}`}
            />
          </div>
          <div role="cell" className="ai-modal__col-text" title={text}>
            {text}
          </div>
          <div role="cell" className="ai-modal__col-value">
            {value}
          </div>
          <div role="cell" className="ai-modal__col-desc" title={descLabel}>
            {descLabel}
          </div>
          <div role="cell" className="ai-modal__col-linkrole" title={linkroleText}>
            <LinkroleCell linkrole={lr} tagLanguage={tagLanguage} alias={linkroleOverrides[lr.uri]?.alias} />
            {winningSibling ? (
              <div className="ai-modal__conflict-hint">
                {t('ai.conflict.linkrole')}: {linkroleLabel(winningSibling, { tagLanguage, alias: linkroleOverrides[winningSibling.uri]?.alias })}
              </div>
            ) : null}
          </div>
          <div role="cell" className="ai-modal__col-concept" title={p.conceptName}>
            {p.conceptName}
          </div>
          <div role="cell" className="ai-modal__col-f1">
            {Math.round(p.f1ScoreTest * 100)} %
          </div>
          <div role="cell" className="ai-modal__col-origin">
            <span className={`ai-origin-pill ai-origin-pill--${applied ? origin : 'ai'}`}>{originText}</span>
          </div>
          <div role="cell" className="ai-modal__col-mand">
            {p.mandatory ? <i className="fa-solid fa-check" aria-label="mandatory" /> : null}
          </div>
          <div role="cell" className="ai-modal__col-chevron" aria-hidden>
            <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`} />
          </div>
        </div>
        {isExpanded && (
          <div className="ai-modal__detail">
            {[
              [t('ai.col.value'), value],
              [t('ai.col.text'), text],
              [t('ai.col.description'), descLabel],
              [t('ai.col.linkrole'), linkroleText],
              [t('ai.col.concept'), p.conceptName],
              [t('ai.col.f1'), `${Math.round(p.f1ScoreTest * 100)} %`],
              [t('ai.col.origin'), originText],
              [t('ai.col.mandatory'), p.mandatory ? '✓' : '—'],
            ].map(([label, val]) => (
              <div className="ai-modal__detail-row" key={label}>
                <span className="ai-modal__detail-label">{label}</span>
                <span className="ai-modal__detail-value">{val}</span>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const VirtualRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const { t } = useTranslation('tag');

    const it = displayRows[index];
    const rowRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (rowRef.current) setSize(index, rowRef.current.getBoundingClientRect().height);
    });
    if (!it) return null;

    if (it.kind === 'empty') {
      return (
        <div role="row" style={style} className={`ai-modal__grid-row${it.muted ? ' ai-modal__grid-row--muted' : ''}`}>
          <div ref={rowRef}>
            <div className="ai-modal__rowmain" style={{ cursor: 'default' }}>
              <div role="cell" className="ai-modal__col-check" />
              <div role="cell" className="ai-modal__col-value">
                {it.value}
              </div>
              <div role="cell" className="ai-modal__col-text" title={it.text}>
                {it.text}
              </div>
              <div role="cell" className="ai-modal__empty">
                {t('ai.no.suggestions')}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (it.kind === 'cell-group') {
      const top = it.top;
      return (
        <div role="row" style={style} className="ai-modal__grid-row ai-modal__group-row">
          <div ref={rowRef}>
            <div className="ai-modal__ghead" onClick={() => toggleGroup(it.key)}>
              <div className="ai-modal__gcol-check" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={!!top && checked.has(top.key)}
                  disabled={!top}
                  onChange={() => top && toggle(top.key)}
                  aria-label={`${it.text} ${it.value}`}
                />
              </div>
              <div className="ai-modal__gcol-chevron" aria-hidden>
                <i className={`fa-solid fa-chevron-${it.expanded ? 'down' : 'right'}`} />
              </div>
              <div className="ai-modal__gcol-a" title={it.rowText}>
                {it.rowText}
              </div>
              <div className="ai-modal__gcol-b" title={it.colText}>
                {it.colText}
              </div>
              <div className="ai-modal__gcol-c">{it.value}</div>
              <div className="ai-modal__gcol-badge">{it.candidateCount > 1 ? <span className="ai-modal__count-badge">{it.candidateCount}</span> : null}</div>
            </div>
          </div>
        </div>
      );
    }

    if (it.kind === 'concept-group') {
      const eligible = conceptGroupEligibleKeys(it.members, checked);
      const state = groupCheckState(eligible, checked);
      const lrText = linkroleLabel(it.lr, { tagLanguage, alias: linkroleOverrides[it.lr.uri]?.alias });
      const countTitle = `${it.cellCount} ${t('ai.group.cells')}${it.appliedCount > 0 ? ` · ${it.appliedCount} ${t('ai.group.applied')}` : ''}`;
      return (
        <div role="row" style={style} className="ai-modal__grid-row ai-modal__group-row">
          <div ref={rowRef}>
            <div className="ai-modal__ghead" onClick={() => toggleGroup(it.key)}>
              <div className="ai-modal__gcol-check" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  ref={(el) => {
                    if (el) el.indeterminate = state === 'some';
                  }}
                  checked={state === 'all'}
                  disabled={eligible.length === 0}
                  onChange={() => toggleConceptGroup(it.members)}
                  aria-label={`${it.conceptName} ${it.lr.uri}`}
                />
              </div>
              <div className="ai-modal__gcol-chevron" aria-hidden>
                <i className={`fa-solid fa-chevron-${it.expanded ? 'down' : 'right'}`} />
              </div>
              <div className="ai-modal__gcol-a" title={it.descLabel}>
                {it.descLabel}
              </div>
              <div className="ai-modal__gcol-b" title={it.conceptName}>
                {it.conceptName}
              </div>
              <div className="ai-modal__gcol-c" title={lrText}>
                <LinkroleCell linkrole={it.lr} tagLanguage={tagLanguage} alias={linkroleOverrides[it.lr.uri]?.alias} />
              </div>
              <div className="ai-modal__gcol-badge">
                {it.expanded ? (
                  <span className="ai-modal__count-badge" title={countTitle}>
                    {it.cellCount}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (it.kind === 'leaf-header') {
      return (
        <div role="row" style={style} className="ai-modal__grid-row ai-modal__subhead-row">
          <div ref={rowRef}>
            <div className="ai-modal__subhead">
              <div role="columnheader" className="ai-modal__col-check" />
              <div role="columnheader" className="ai-modal__col-text">
                {t('ai.col.text')}
              </div>
              <div role="columnheader" className="ai-modal__col-value">
                {t('ai.col.value')}
              </div>
              <div role="columnheader" className="ai-modal__col-desc">
                {t('ai.col.description')}
              </div>
              <div role="columnheader" className="ai-modal__col-linkrole">
                {t('ai.col.linkrole')}
              </div>
              <div role="columnheader" className="ai-modal__col-concept">
                {t('ai.col.concept')}
              </div>
              <div role="columnheader" className="ai-modal__col-f1">
                {t('ai.col.f1')}
              </div>
              <div role="columnheader" className="ai-modal__col-origin">
                {t('ai.col.origin')}
              </div>
              <div role="columnheader" className="ai-modal__col-mand">
                {t('ai.col.mandatory')}
              </div>
              <div role="columnheader" className="ai-modal__col-chevron" aria-hidden />
            </div>
          </div>
        </div>
      );
    }

    const applied = it.applied;
    const siblingChecked = it.rows.some((other: Linkrole) => other.uri !== it.lr.uri && checked.has(rowKey(it.row, it.col, it.p.conceptId, other.uri)));
    const className = ['ai-modal__grid-row', applied ? 'ai-modal__row--applied' : '', siblingChecked ? 'ai-modal__row--excluded' : '']
      .filter(Boolean)
      .join(' ');
    return (
      <div role="row" style={style} className={className}>
        <div ref={rowRef}>{renderPredRow(it)}</div>
      </div>
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, height: '100%' }}>
          <Spinner />
          <div style={{ fontSize: 20, textAlign: 'center' }}>{t('ai.loading')}</div>
        </div>
      );
    }
    if (error) {
      const code = error instanceof ApiError ? error.code : (error as { request?: { data?: { code?: string } } })?.request?.data?.code;
      return (
        <div className="ai-modal__status">
          {code === 'AI_WARMING' ? t('ai.warming') : t('ai.error')}
          <Button variant="link" onClick={runPredict}>
            {t('ai.retry')}
          </Button>
        </div>
      );
    }
    if (!data) {
      return (
        <>
          <div className="ai-modal__pick-title">
            {t('ai.pick.tabletitle')}: <strong>{displayTitle || '—'}</strong>
          </div>
          <div className="ai-modal__pick-info">
            {t('ai.pick.instructions')} <strong>{requestCells.length}</strong> {t('ai.pick.count')}
          </div>
          {cappedLocally ? (
            <div className="ai-modal__truncated" role="alert">
              <i className="fa-solid fa-triangle-exclamation" /> {t('ai.truncated')}
            </div>
          ) : null}
          {linkroleFilterControl}
          <div className="ai-modal__picker" onClick={handlePickClick}>
            <StyledDataSheet data={sheetData} withTags={false} selected={null} onSelect={() => {}} />
          </div>
        </>
      );
    }

    if (filteredToEmpty) {
      return (
        <>
          {linkroleFilterControl}
          <div className="ai-modal__status" role="status">
            {t('ai.results.noneMatch')}
          </div>
        </>
      );
    }

    return (
      <>
        {data.truncated || cappedLocally ? (
          <div className="ai-modal__truncated" role="alert">
            <i className="fa-solid fa-triangle-exclamation" /> {t('ai.truncated')}
          </div>
        ) : null}
        {linkroleFilterControl}
        <AiResultTabs activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="ai-modal__grid-scroll">
          <div className="ai-modal__grid" role="table">
            {activeTab === 'lista' ? (
              <div className="ai-modal__grid-head" role="row">
                <div role="columnheader" className="ai-modal__col-check">
                  {selectAllCheckbox}
                </div>
                <div role="columnheader" className="ai-modal__col-text">
                  {t('ai.col.text')}
                </div>
                <div role="columnheader" className="ai-modal__col-value">
                  {t('ai.col.value')}
                </div>
                <div role="columnheader" className="ai-modal__col-desc">
                  {t('ai.col.description')}
                </div>
                <div role="columnheader" className="ai-modal__col-linkrole">
                  {t('ai.col.linkrole')}
                </div>
                <div role="columnheader" className="ai-modal__col-concept">
                  {t('ai.col.concept')}
                </div>
                <div role="columnheader" className="ai-modal__col-f1">
                  {t('ai.col.f1')}
                </div>
                <div role="columnheader" className="ai-modal__col-origin">
                  {t('ai.col.origin')}
                </div>
                <div role="columnheader" className="ai-modal__col-mand">
                  {t('ai.col.mandatory')}
                </div>
                <div role="columnheader" className="ai-modal__col-chevron" aria-hidden />
              </div>
            ) : (
              <div className="ai-modal__grid-head" role="row">
                <div role="columnheader" className="ai-modal__gcol-check">
                  {selectAllCheckbox}
                </div>
                <div role="columnheader" className="ai-modal__gcol-chevron" aria-hidden />
                <div role="columnheader" className="ai-modal__gcol-a">
                  {activeTab === 'rad' ? t('ai.col.row') : t('ai.col.description')}
                </div>
                <div role="columnheader" className="ai-modal__gcol-b">
                  {activeTab === 'rad' ? t('ai.col.column') : t('ai.col.concept')}
                </div>
                <div role="columnheader" className="ai-modal__gcol-c">
                  {activeTab === 'rad' ? t('ai.col.value') : t('ai.col.linkrole')}
                </div>
                <div className="ai-modal__gcol-badge" aria-hidden />
              </div>
            )}
            <List
              ref={listRef}
              className="ai-modal__list"
              height={LIST_HEIGHT}
              width="100%"
              itemCount={displayRows.length}
              itemSize={getItemSize}
              overscanCount={6}
            >
              {VirtualRow}
            </List>
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal show={show} onHide={onClose} animation={false} dialogClassName="ai-modal-dialog">
      <Modal.Header closeButton>
        <Modal.Title>
          <Typography variant="body-8" fontWeight="semibold">
            {t('ai.modal.title')}
          </Typography>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderBody()}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t('ai.cancel')}
        </Button>
        {data ? (
          <>
            <Button variant="secondary" onClick={() => reset()}>
              {t('ai.back')}
            </Button>
            <Button variant="primary" disabled={checkedCount === 0 || !taxonomyId} onClick={handleApply}>
              {t('ai.apply')}
            </Button>
          </>
        ) : (
          <Button variant="primary" disabled={requestCells.length === 0 || isLoading} onClick={runPredict}>
            {t('ai.fetch')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
